import { HttpErrorResponse } from '@angular/common/http';

/**
 * Extracts a human-readable error message from an API error response (DOCU-663).
 *
 * FastEndpoints returns validation and binding failures as
 * `{ statusCode, message, errors: { "<propertyPath>": ["msg", ...] } }`.
 * One line per property error; falls back to the body message or the
 * generic HTTP message when no structured body is present.
 */
export function extractApiErrorMessage(
  err: unknown,
  fallback: string,
  /**
   * DOCU-634: endpoints that add errors without a property (`AddError("…")`) file them
   * under "generalErrors"; prefixing that on screen only adds noise.
   */
  includePropertyName = true,
): string {
  if (err instanceof HttpErrorResponse && err.error && typeof err.error === 'object') {
    const body = err.error as { message?: unknown; errors?: Record<string, unknown> };

    if (body.errors && typeof body.errors === 'object') {
      const lines = Object.entries(body.errors).flatMap(([property, messages]) =>
        (Array.isArray(messages) ? messages : [messages])
          .filter((m): m is string => typeof m === 'string')
          .map(m => (includePropertyName ? `${property}: ${m}` : m))
      );
      if (lines.length > 0) {
        return lines.join('\n');
      }
    }

    if (typeof body.message === 'string' && body.message.trim()) {
      return body.message;
    }
  }

  const message = (err as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}
